import { icons, ProcessTypes } from "../../utils/icons";
import {
  isRangeSelectionAllowed,
  isFormula,
  isFormulaComplete,
  autoCompleteFormula,
  decodeTableRefrencing,
  needsDecoding,
  getRefrenceAroundCursor,
  isRange,
  isCell,
  replaceAroundCursor,
  cycleCellReferenceType,
  toggleTableReferenceAbsRel,
  extractWords,
} from "../../utils/util";
import { hfInstance } from "../../store/Cells";
import { EditModes, HotkeyScopes, keycodeToDirection } from "../../utils/constants";
import { parameters, CompositeFunctions } from "./formula/FormulaConstants";
import { TableHelper } from "../../store/helpers/TableHelper";
import hotkeys from "hotkeys-js";

import {
  getFractionNumber,
  getTimeNumber,
  isValidCurrency,
  isValidFraction,
  isValidPercentage,
  isValidTime,
  NumberFormats,
  VALID_DATE_FORMATS,
  VALID_TIME_FORMATS,
} from "../../utils/DataTypeUtils";
import { parseValue } from "numfmt";
export default {
  data() {
    return {
      text: "",
      formulaText: "",
      originalText: "",
      cursorPosition: 0,
      showSearchBox: false,
      id: null,
      collection: [],
      argNumber: 0,
      dynamicWidth: 90,
      dynamicHeight: 20,
      icons: icons,
      ctrlEnter: false,
      isSaved: false, // isSaved used for cases where we save without blur to have the right order for undo/redo and prevent saving again
      EditModes,
    };
  },
  computed: {
    editCell() {
      return this.$store.state.cells.editCell;
    },
    inputText() {
      return this.formulaText.substring(0, this.$refs.textarea?.selectionStart).match(/[a-zA-Z_.0-9]*$/g)[0];
    },
    dropDownFilteredList() {
      return this.collection?.filter((item) => {
        return item.title.toLowerCase().startsWith(this.inputText.toLowerCase());
      });
    },
    allFormulaNames() {
      return hfInstance
        .getRegisteredFunctionNames()
        .sort((a, b) => a.localeCompare(b))
        .map((item) => ({ title: item, icon: "icon-function-variant" }));
    },
    tableNameRanges() {
      let tables = [];
      let tableNames = this.$store.getters["table/getAllTableNameRanges"]();
      tableNames.forEach((element) => {
        element = this.$store.getters["table/getLocalNameRangeByDefault"](element);
        tables.push({ title: element, icon: "mdi mdi-table", type: "table" });
      });
      return tables;
    },
    showSearch() {
      const textarea = this.$refs.textarea;
      const [functionName] = this.findFunctionAndArgument(this.text, textarea?.selectionStart);
      const cursorPosition = textarea.selectionStart;

      const [beforeCursor, afterCursor] = [
        textarea.value.substring(0, cursorPosition),
        textarea.value.substring(cursorPosition),
      ];
      const [openQuoteIndex, closeQuoteIndex] = [beforeCursor.lastIndexOf('"'), afterCursor.indexOf('"')];

      const isInsideQuotes = openQuoteIndex !== -1 && closeQuoteIndex !== -1;

      const isEvenQuotesCount = (this.text.split('"').length - 1) % 2 === 0;
      const isCursorAtWordBoundary = textarea.value[textarea.selectionStart - 1]?.match(/[a-zA-Z_.0-9]*$/g)[0];
      const textBeforeCursor = beforeCursor.trim();
      const lastPart = textBeforeCursor
        .split(/[,;=+\-/*():]/) // Split by operators and delimiters
        .pop() // Get the last part
        .trim(); // Remove whitespace

      const isNumberBeforeCursor = /^\d+$/.test(lastPart);
      return (
        (isFormula(this.text) &&
          isEvenQuotesCount &&
          !isInsideQuotes &&
          isCursorAtWordBoundary &&
          !isNumberBeforeCursor &&
          !isRangeSelectionAllowed(this.text)) ||
        this.isCompositeFunction(functionName)
      );
    },
    cellId() {
      return this.mergedCellIds[this.activeCellId]
        ? this.mergedCellIds[this.activeCellId].replace("_merged", "")
        : this.activeCellId;
    },
    rowId() {
      return this.activeCellId.split("_")[0];
    },
    columnId() {
      return this.activeCellId.split("_")[1];
    },
    rowIndex() {
      return this.rowMap[this.rowId].index;
    },
    columnIndex() {
      return this.columnMap[this.columnId].index;
    },
    activeCellId() {
      return this.$store.state.cells.activeCellId;
    },
    isFormulaMode() {
      return this.editCell.formulaRangeSelectionAllowed && this.editCell.text.trim().startsWith("=");
    },
  },
  methods: {
    async blur(e) {
      if (this.isSaved) return;
      await this.saveCellContent(e.target.dataset.id, e.target.value);
    },
    selectFormula(item) {
      if (!item) return;
      const textarea = this.$refs.textarea;
      const beforeSelection = this.formulaText.substring(0, textarea?.selectionStart);
      const afterSelection = this.formulaText.substring(textarea?.selectionStart);
      const wordBeforeCursor = beforeSelection.match(/[a-zA-Z_.0-9]*$/g)[0] || "";

      let newFormula = "";
      if (item.type == "table" || item.type == "column" || item.type == "atRow") {
        newFormula = item.title;
      } else {
        newFormula = (item.value ?? item.title + "(") + "";
      }
      const text =
        (wordBeforeCursor ? beforeSelection.slice(0, -wordBeforeCursor.length) : beforeSelection) +
        newFormula +
        afterSelection;
      // Calculate the new cursor position and set the cursor position dynamically
      const newCursorPosition = beforeSelection.length - wordBeforeCursor.length + newFormula.length;

      this.$store.dispatch("cells/setEditCell", {
        cursorPosition: newCursorPosition,
        formulaRangeSelectionAllowed: isRangeSelectionAllowed(text.substring(0, newCursorPosition)),
        text,
      });
      this.$store.dispatch("cells/setFormulaRange", { title: text, isMainRange: false });
      this.text = text;
      if (item.type == "table" || item.type == "column" || item.type == "atRow") return;
      const [, arg] = this.findFunctionAndArgument(text, newCursorPosition);
      this.argNumber = arg;

      this.collection = this.isCompositeFunction(item.title)
        ? parameters.filter(
            (x) =>
              x.function === item.title.toLowerCase() &&
              CompositeFunctions.includes(x.function) &&
              x.parameterNo === this.argNumber
          )
        : [...this.allFormulaNames];

      if (!this.isCompositeFunction(item.title)) this.showSearchBox = false;
      this.$refs.textarea.focus(); // for selecting formula with mouse click.
    },
    getCellDataType(cellId) {
      const [columnIndex, rowIndex] = this.getCellIndexById(cellId);
      return hfInstance.getCellValueDetailedType({
        sheet: this.currentSheet.sheetId,
        row: rowIndex,
        col: columnIndex,
      });
    },
    insertNewLine(event) {
      const textarea = event.target;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      this.text = this.text.substring(0, start) + "\n" + this.text.substring(end);
      this.$nextTick(() => {
        textarea.setSelectionRange(end + 1, end + 1);
      });
      event.preventDefault();
    },
    async saveCellContent(cellId, text) {
      // updating header of table
      if (this.current.type === ProcessTypes.Grid && this.tableHeaders[cellId]) {
        return await this.$store.dispatch("table/validateAndUpdateHeader", {
          text,
          cellId,
          originalText: this.originalText,
        });
      }
      if (text) {
        await this.$store.dispatch("table/appendRowOrColumnToTable", cellId);
      }

      if (isFormula(text) && !isFormulaComplete(text)) text = autoCompleteFormula(text);
      let localText = text;
      let tables = [];
      let tableNames = this.$store.getters["table/getAllTableNameRanges"]();
      tableNames.forEach((element) => {
        element = this.$store.getters["table/getLocalNameRangeByDefault"](element);
        tables.push(element);
      });
      if (needsDecoding(text)) {
        // convert data from table[columns] to table.columns
        const { nameRanges, decodedTitle } = decodeTableRefrencing(
          text,
          cellId,
          this.$store.getters,
          this.$store.state,
          TableHelper.inRowNameRangeFunction
        );

        // take name range from local mapping
        localText = decodedTitle;
        nameRanges.forEach((element) => {
          localText = localText.replace(
            element.decoded,
            this.$store.getters["table/getDefaultNameRangeByLocal"](element.decoded)
          );
        });
        await this.$store.dispatch("cells/editCellMetaData", { id: cellId, key: "formula", value: text });
      } else if (needsDecoding(text, tables)) {
        const wordsArray = extractWords(text);
        const matches = wordsArray.filter((word) => tables.includes(word));
        localText = text;
        matches.forEach((element) => {
          localText = localText.replace(element, this.$store.getters["table/getDefaultNameRangeByLocal"](element));
        });

        await this.$store.dispatch("cells/editCellMetaData", { id: cellId, key: "formula", value: text });
      } else if (this.current.design.cellsMetaData[cellId]?.formula) {
        delete this.current.design.cellsMetaData[cellId]?.formula;
      }

      cellId = this.mergedCellIds[cellId] ? this.mergedCellIds[cellId].replace("_merged", "") : cellId;

      if (this.ctrlEnter || this.$store.getters["cells/isCellDisabled"](cellId)) {
        !this.ctrlEnter &&
          this.$store.dispatch("addToast", {
            severity: "error",
            life: 2000,
            summary: "Sorry! You are trying to edit disabled cell.",
          });
        return (this.ctrlEnter = false);
      }
      if (!isFormula(localText)) localText = this.setDataType(cellId, localText);
      await this.$store.dispatch("cells/editCellContent", { id: cellId, value: localText });
      if (isFormula(localText)) {
        const formulaRange = await this.$store.dispatch("table/applyFormulaForAColumn", cellId);
        this.setDataTypeOfFormulaCell(cellId, formulaRange);
      }
    },
    setRange(event) {
      const value = event.target.value;

      if (isFormula(value)) {
        this.formulaText = value;
        this.cursorPosition = event.target.selectionStart - 1;
        if (event.key.length === 1) {
          this.$store.dispatch("cells/setFormulaRange", { title: value ?? "", isMainRange: false });
          this.$store.dispatch("cells/setActiveRangeId", this.mainRange.id);
        }
      } else {
        this.formulaText = "";
        this.cursorPosition = 0;
      }
    },
    isCompositeFunction(funcName) {
      return (
        CompositeFunctions.includes(funcName.toLowerCase()) &&
        parameters.some((x) => x.function === funcName.toLowerCase() && x.parameterNo === this.argNumber)
      );
    },
    findFunctionAndArgument(expression, cursorPosition) {
      let arg = 0;
      let bracket = 0;
      let functionName = "";
      let i = cursorPosition;

      for (; i > 0; i--) {
        if (expression[i] === ")") {
          bracket++;
        } else if (expression[i] === "(") {
          if (bracket > 0) bracket--;
          else {
            let x = i - 1;
            let funcName = "";
            while (x >= 0 && expression[x].match(/[a-zA-Z_.0-9]/)) {
              funcName = expression[x] + funcName;
              x--;
            }
            if (funcName) {
              functionName = funcName;
              break;
            }
          }
        } else if (expression[i] === "," && bracket === 0) {
          arg++;
        }
      }
      return [functionName, arg, i];
    },
    findTableReference(expression, cursorPosition) {
      let openBraceIndex = -1;
      let unclosedBraces = 0;

      // Find the first open brace which is not closed before the cursor position
      for (let i = 0; i < cursorPosition; i++) {
        if (expression[i] === "[") {
          if (unclosedBraces === 0) {
            openBraceIndex = i;
          }
          unclosedBraces++;
        } else if (expression[i] === "]") {
          if (unclosedBraces > 0) {
            unclosedBraces--;
            if (unclosedBraces === 0) {
              openBraceIndex = -1;
            }
          }
        }
      }

      if (openBraceIndex === -1) {
        return { tableName: null, atSingExists: null }; // No unmatched open brace found
      }

      // Work backwards from the open brace to find the table name
      let tableName = "";
      for (let i = openBraceIndex - 1; i >= 0; i--) {
        if (/[a-zA-Z0-9_]/.test(expression[i])) {
          tableName = expression[i] + tableName;
        } else {
          break;
        }
      }
      const atSingExists = expression[openBraceIndex + 1] === "@";
      return { tableName: tableName, atSingExists: atSingExists }; // Return null if no table name found
    },
    setDataType(cellId, text, customRange) {
      const [columnIndex, rowIndex] = this.getCellIndexById(cellId);
      const currentDataType = this.$store.getters["rangeStyle/dataType"](columnIndex, rowIndex);
      customRange = customRange ?? [columnIndex, rowIndex, columnIndex, rowIndex];
      let dataType = currentDataType;
      switch (currentDataType) {
        case NumberFormats.General: {
          const parsed = parseValue(text);
          if (isValidCurrency(text)) {
            dataType = NumberFormats.Currency;
            text = parsed.v;
          } else if (isValidPercentage(text)) {
            dataType = NumberFormats.Percentage;
            text = parsed.v;
          } else if (isValidFraction(text)) {
            dataType = NumberFormats.Fraction;
            text = getFractionNumber(text);
          } else if (parsed && VALID_DATE_FORMATS.includes(parsed.z)) {
            text = parsed.v;
            dataType = NumberFormats.ShortDate;
          } else if (parsed && VALID_TIME_FORMATS.includes(parsed.z)) {
            text = parsed.v;
            dataType = NumberFormats.Time;
          } else if (parsed?.v) {
            text = parsed.v;
          }
          break;
        }
        case NumberFormats.ShortDate:
        case NumberFormats.LongDate: {
          const parsed = parseValue(text);
          if (parsed && VALID_DATE_FORMATS.includes(parsed.z)) {
            text = parsed.v;
          }
          break;
        }
        case NumberFormats.Percentage: {
          const parsed = parseValue(text);
          if (isValidPercentage(text)) {
            text = parsed.v;
          }
          break;
        }
        case NumberFormats.Fraction: {
          if (isValidFraction(text)) text = getFractionNumber(text);
          break;
        }
        case NumberFormats.Time: {
          if (isValidTime(text)) text = getTimeNumber(text);
          break;
        }
      }
      if (dataType && dataType !== NumberFormats.General)
        this.$store.dispatch("rangeStyle/updateRangeStyle", {
          customRange,
          data: { dataType },
        });
      return text;
    },
    setDataTypeOfFormulaCell(cellId, range) {
      const [columnIndex, rowIndex] = this.getCellIndexById(cellId);
      const currentDataType = this.$store.getters["rangeStyle/dataType"](columnIndex, rowIndex);
      const formulaDataType = this.getCellDataType(cellId);
      const hyperformulaToCustomDataType = {
        NUMBER_DATE: NumberFormats.ShortDate,
        NUMBER_TIME: NumberFormats.Time,
      };
      if (currentDataType === NumberFormats.General && ["NUMBER_DATE", "NUMBER_TIME"].includes(formulaDataType)) {
        this.$store.dispatch("rangeStyle/updateRangeStyle", {
          customRange: range ?? [columnIndex, rowIndex, columnIndex, rowIndex],
          data: { dataType: hyperformulaToCustomDataType[formulaDataType] },
        });
      }
    },
    editCellText(e) {
      e.preventDefault();
      this.text = e.target.value;
      const cursorPosition = e.target.selectionStart;
      const formulaRangeSelectionAllowed = isRangeSelectionAllowed(this.text.substring(0, e.target.selectionStart));
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", this.getCellCoordinate(this.activeCellId));
      this.$store.dispatch("cells/setEditCell", {
        text: this.text,
        formulaRangeSelectionAllowed,
        range: "",
        cursorPosition,
      });
      const [functionName, arg] = this.findFunctionAndArgument(this.text, cursorPosition);
      //TODO: if it has @ show only columns
      let { tableName, atSingExists } = this.findTableReference(this.text, cursorPosition);
      tableName = this.$store.getters["table/getDefaultNameRangeByLocal"](tableName?.toLowerCase());
      let tableColumnNames = [];
      if (tableName) {
        const columnNames = this.$store.getters["table/getTableColumnNameRanges"]({
          tableName: tableName,
          onlyColumns: atSingExists,
        });
        if (columnNames.length > 0 && !atSingExists) {
          tableColumnNames.push({ title: "@", type: "atRow" });
        }
        columnNames.forEach((element) => {
          tableColumnNames.push({ title: element, type: "column" });
        });
      }
      this.argNumber = arg;
      this.collection = tableName
        ? tableColumnNames
        : this.isCompositeFunction(functionName)
        ? parameters.filter(
            (x) =>
              x.function === functionName.toLowerCase() &&
              CompositeFunctions.includes(x.function) &&
              x.parameterNo === this.argNumber
          )
        : [...this.allFormulaNames];

      if (!tableName && !this.isCompositeFunction(functionName)) {
        this.collection = [...this.collection, ...this.tableNameRanges];
      }
      this.showSearchBox = tableName ? true : this.showSearch;
    },
    handleHotkeys(event) {
      if (event.ctrlKey && event.code === "Space" && this.editMode !== EditModes.InnerEdit) {
        event.preventDefault();
        hotkeys.trigger("ctrl+space", HotkeyScopes.ProcessDesigner);
        return true;
      }
      if (event.shiftKey && event.code === "Space" && this.editMode !== EditModes.InnerEdit) {
        event.preventDefault();
        hotkeys.trigger("shift+space", HotkeyScopes.ProcessDesigner);
        return true;
      }
      if (event.ctrlKey && event.code === "KeyA" && this.editMode !== EditModes.InnerEdit) {
        event.preventDefault();
        hotkeys.trigger("ctrl+a", HotkeyScopes.ProcessDesigner);
        return true;
      }
      return false;
    },
    handleF4Key(event) {
      if (this.editMode !== EditModes.Disable) {
        if (!this?.mainRange?.range) return;
        let extraFormats = event.shiftKey
          ? ["A10:B30", "A10", "$A$10:$B$30", "$A$10", "$A10:$B30", "$A10", "A$10:B$30", "A$10"]
          : ["A10", "A10:B30", "$A$10", "$A$10:$B$30", "$A10", "$A10:$B30", "A$10", "A$10:B$30"];

        let cursorPosition = event.target.selectionStart;
        let text = event.target.value;
        const tempText = getRefrenceAroundCursor(text, cursorPosition, extraFormats);

        if (isRange(tempText) || isCell(tempText)) {
          const absoluteText = cycleCellReferenceType(tempText);
          const { newText, cursorPos } = replaceAroundCursor(text, cursorPosition, tempText, absoluteText);
          text = newText;
          cursorPosition = cursorPos;
        } else {
          const convertedText = toggleTableReferenceAbsRel(tempText);
          if (convertedText.length > tempText.length) {
            cursorPosition++;
          } else if (convertedText.length < tempText.length) {
            cursorPosition--;
          }
          const { newText, cursorPos } = replaceAroundCursor(text, cursorPosition, tempText, convertedText);
          text = newText;
          cursorPosition = cursorPos;
        }

        this.$store.dispatch("cells/setEditCell", { text, cursorPosition });
      }
    },
    async handleTabKey(event) {
      event.preventDefault();
      if (this.showSearchBox) {
        hotkeys.trigger("tab", HotkeyScopes.FormulaSearch);
      } else if (event.shiftKey) {
        await this.saveCellContent(this.activeCellId, this.text);
        this.isSaved = true; // to have to correct order first we save the content then trigger range selection
        hotkeys.trigger("shift+tab", HotkeyScopes.ProcessDesigner);
      } else {
        await this.saveCellContent(this.activeCellId, this.text);
        this.isSaved = true;
        hotkeys.trigger("tab", HotkeyScopes.ProcessDesigner);
      }
    },
    async handleBasicDirectionKeys(event) {
      if (event.shiftKey && event.ctrlKey && this.editMode !== EditModes.InnerEdit) {
        event.preventDefault();
        if (!this.editCell.formulaRangeSelectionAllowed) {
          this.$store.dispatch("pdesign/setEditMode", EditModes.Disable);
        }
        hotkeys.trigger("ctrl+shift+" + keycodeToDirection[event.key], HotkeyScopes.ProcessDesigner);

        return true;
      }

      // Handle Shift + Direction
      if (event.shiftKey && this.editMode !== EditModes.InnerEdit) {
        event.preventDefault();
        if (!this.editCell.formulaRangeSelectionAllowed) {
          this.$store.dispatch("pdesign/setEditMode", EditModes.Disable);
        }
        hotkeys.trigger("shift+" + keycodeToDirection[event.key], HotkeyScopes.ProcessDesigner);
        return true;
      }

      // Handle Direction Keys in Outer Edit Mode (Without Ctrl)
      if (this.editMode === EditModes.OuterEdit && !event.ctrlKey) {
        if (this.tableHeaders[this.editCell.id]) this.reset();
        event.preventDefault();
        if (hotkeys.getScope() === HotkeyScopes.FormulaSearch) {
          hotkeys.trigger(keycodeToDirection[event.key], HotkeyScopes.FormulaSearch);
        } else {
          if (!this.isFormulaMode) {
            await this.saveCellContent(this.activeCellId, this.text);
            this.isSaved = true;
          }
          hotkeys.trigger(keycodeToDirection[event.key], HotkeyScopes.ProcessDesigner);
        }
        return true;
      }

      // Handle Ctrl + Direction
      if (this.editMode === EditModes.OuterEdit && event.ctrlKey) {
        event.preventDefault();
        hotkeys.trigger("ctrl+" + keycodeToDirection[event.key], HotkeyScopes.ProcessDesigner);
        return true;
      }

      return false;
    },
    async processCtrlEnter() {
      this.ctrlEnter = true;
      if (!isFormula(this.text)) {
        this.text = this.setDataType(this.editCell.id, this.text, this.mainRange?.range);
      }

      await this.$store.dispatch("cells/updateSelectedCells", {
        range: this.mainRange.range,
        value: this.text,
      });

      this.$store.dispatch("cells/setEditCell", {
        formulaRangeSelectionAllowed: false,
        range: "",
      });

      if (isFormula(this.text)) {
        this.setDataTypeOfFormulaCell(this.editCell.id, this.mainRange?.range);
      }
    },
  },
};
